import { useEffect } from "react";
import { useLocation, useNavigate, Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import config from "@/i18n/config";
import { detectLanguageFromURL, stripLangPrefix, translateFullPath } from "@/i18n/detectLanguage";
import AppErrorBoundary from "@/components/AppErrorBoundary";

/**
 * LanguageRouter — detects language from URL (prefix, subdomain, or dedicated domain)
 * and syncs i18next accordingly.
 *
 * Responsibilities:
 *  1. Sync i18n language with URL
 *  2. Redirect /en/... → /... (default language without prefix)
 *  3. Redirect English slugs → localized slugs for the detected language
 *     e.g. /fr/memberships → /fr/adhesions (prefix mode)
 *          /memberships on thailandelite.fr → /adhesions (domain mode)
 *     e.g. /fr/guides/stay-extension → /fr/guides/extension-sejour-de-1-an
 */
const LanguageRouter = () => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const detectedLang = detectLanguageFromURL();
    const targetLang = detectedLang || config.defaultLanguage;

    if (i18n.language !== targetLang) {
      i18n.changeLanguage(targetLang);
    }

    const pathname = location.pathname;

    // ── Redirect /en/... → /... (default language prefix removal) ──────────
    if (config.mode === "prefix") {
      const segments = pathname.split("/").filter(Boolean);
      if (
        segments.length > 0 &&
        segments[0].toLowerCase() === config.defaultLanguage
      ) {
        const stripped = "/" + segments.slice(1).join("/") || "/";
        navigate(stripped, { replace: true });
        return;
      }
    }

    // ── Redirect English slugs → localized slugs ────────────────────────────
    // Only for non-default languages that have localized slugs
    if (targetLang === config.defaultLanguage) return;

    // Get the sub-path (strip lang prefix if present in URL)
    const subPath = stripLangPrefix(pathname);

    // Translate sub-path to localized slug
    const localizedPath = translateFullPath(subPath, targetLang);

    // If the localized path differs, redirect
    if (localizedPath !== subPath) {
      // Determine if the URL uses a lang prefix or domain-based routing
      const hasPrefixInUrl =
        pathname.startsWith(`/${targetLang}/`) ||
        pathname === `/${targetLang}`;

      const redirectTarget = hasPrefixInUrl
        ? `/${targetLang}${localizedPath}`
        : localizedPath;

      navigate(redirectTarget, { replace: true });
    }
  }, [location.pathname, i18n, navigate]);

  return (
    <AppErrorBoundary context="site" resetKey={location.pathname + location.search}>
      <Outlet />
    </AppErrorBoundary>
  );
};

export default LanguageRouter;
